import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { projects, faqs, caseStudies } from "@/lib/data";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function ProjectsSection() {
    const items = caseStudies.slice(0, 2);
    if (items.length === 0) return null;
    return (
        <section data-testid="projects-section" className="border-t border-line bg-bg-alt">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32">
                <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <p className="eyebrow mb-6"><span className="text-gold">●</span> Selected work</p>
                        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] max-w-3xl">Recent commercial installations across Metro Vancouver.</h2>
                    </div>
                    <Link to="/projects" className="hidden md:inline-flex items-center gap-2 text-sm text-ink link-underline">All projects <ArrowUpRight className="w-4 h-4" /></Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {items.map((cs, idx) => (
                        <Link
                            key={cs.slug}
                            to={`/projects/${cs.slug}`}
                            className="group border border-line bg-bg hover:bg-ink hover:text-bg transition-colors duration-500 flex flex-col"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-ink">
                                <img
                                    src={cs.cover}
                                    alt={`${cs.client} — ${cs.title}`}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                                />
                                <span className="absolute top-4 left-4 bg-gold text-ink text-[10px] tracking-widest font-mono px-2 py-1 uppercase">
                                    Case Study · {String(idx + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <div className="p-8 md:p-10 flex flex-col flex-1">
                                <p className="tick text-muted group-hover:text-bg/60 mb-5">{cs.industry} · {cs.location}</p>
                                <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[0.95] mb-3">{cs.client}</h3>
                                <p className="text-sm text-muted group-hover:text-bg/75 mb-6">{cs.title}</p>
                                <p className="text-sm text-ink/80 group-hover:text-bg/85 leading-relaxed mb-8 flex-1">
                                    {cs.overview.slice(0, 170)}…
                                </p>
                                <div className="flex items-center justify-between border-t border-line group-hover:border-bg/25 pt-6">
                                    <span className="text-sm">Read the case study</span>
                                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-10 md:hidden">
                    <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-ink link-underline">All projects <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
            </div>
        </section>
    );
}

export function FaqSection({ items }) {
    const list = items || faqs;
    return (
        <section data-testid="faq-section" className="border-t border-line">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <p className="eyebrow mb-6">Frequently asked</p>
                    <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95] mb-6">Questions we hear before every project.</h2>
                    <p className="text-muted text-sm max-w-sm">Something not covered? Include it in the site assessment form and we'll answer directly.</p>
                </div>
                <div className="md:col-span-8">
                    <Accordion type="single" collapsible className="border-t border-line">
                        {list.map((f, i) => (
                            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-line">
                                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-lg md:text-xl tracking-tight text-ink hover:text-gold transition-colors py-6">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-muted leading-relaxed pb-6">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}

export function ReferralPartner() {
    const audiences = ["General contractors", "Interior designers", "Architects", "Clinic builders", "Commercial brokers", "Property managers", "Developers"];
    const help = [
        "Early technology planning",
        "Scope development",
        "Site coordination",
        "Equipment installation",
        "Vendor communication",
        "Testing and handover",
        "Post-opening support",
    ];
    return (
        <section data-testid="referral" className="bg-panel text-bg grain grain-dark">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-6">
                    <p className="eyebrow text-bg/40 mb-6">Referral partners</p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] mb-6">A dependable technology partner for your commercial projects.</h2>
                    <p className="text-bg/70 mb-8 max-w-md leading-relaxed">Introduce us while the build-out is still on paper. We'll help keep the technology piece from becoming a fire drill in the final two weeks.</p>
                    <Link to="/contact" className="inline-flex items-center gap-2 bg-bg text-ink px-6 py-4 text-sm hover:bg-accent hover:text-bg transition-colors duration-300">Discuss a Referral Partnership <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
                <div className="md:col-span-3">
                    <p className="eyebrow text-bg/40 mb-5">Who we work with</p>
                    <ul className="space-y-3 text-bg/85 text-sm">
                        {audiences.map((a) => <li key={a} className="border-b border-line-2 pb-3">— {a}</li>)}
                    </ul>
                </div>
                <div className="md:col-span-3">
                    <p className="eyebrow text-bg/40 mb-5">How we assist</p>
                    <ul className="space-y-3 text-bg/85 text-sm">
                        {help.map((a) => <li key={a} className="border-b border-line-2 pb-3">→ {a}</li>)}
                    </ul>
                </div>
            </div>
        </section>
    );
}

export function FinalCTA() {
    return (
        <section data-testid="final-cta" className="border-t border-line">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10 items-end">
                <div className="md:col-span-8">
                    <p className="eyebrow mb-6">The next step</p>
                    <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] text-ink">Let's make your business technology easier to manage.</h2>
                </div>
                <div className="md:col-span-4">
                    <p className="text-muted mb-8 leading-relaxed">Tell us what you're opening, upgrading or wrestling with. We'll reply with the next practical step — a walkthrough, a rough scope, or a straight answer.</p>
                    <div className="flex flex-col gap-3">
                        <Link to="/contact" data-testid="final-cta-assess" className="inline-flex items-center justify-between gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors">Request a Site Assessment <ArrowUpRight className="w-4 h-4" /></Link>
                        <a href="tel:+16046714247" data-testid="final-cta-call" className="inline-flex items-center justify-between gap-2 border border-ink text-ink px-6 py-4 text-sm hover:bg-ink hover:text-bg transition-colors font-mono tracking-widest">Call 604-671-4247 <ArrowUpRight className="w-4 h-4" /></a>
                        <Link to="/support" data-testid="final-cta-support" className="inline-flex items-center justify-between gap-2 text-sm text-muted hover:text-ink transition-colors pt-1">Or send a support request <ArrowUpRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
